import { useSearch } from '@diamond/shared/context';
import { apiInstance, buyerCaptureException } from '@diamond/shared/utils';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import cleanDeep from 'clean-deep';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

interface QueryStringSearch {
  [key: string]: string | number | boolean | null;
}

type QueryOptions = {
  enabled?: boolean;
  staleTime?: number;
  prefetchNextPage?: boolean;
};

export function QueryWrapperNext(
  url: string,
  token?: string,
  options?: Record<string, unknown>
) {
  const router = useRouter();
  //page default value
  const page = 1;

  function useQueryGet<K>(
    queryKey: string,
    params?: Readonly<QueryStringSearch>
  ) {
    const fetchData = async () => {
      try {
        const res = await apiInstance(token).get(url, {
          params: cleanDeep(params),
        });
        return res.data;
      } catch (error) {
        if (error instanceof AxiosError) {
          buyerCaptureException(error.response?.data.message, (scope) => {
            scope.setTag(queryKey, 'useQueryGet error');
            scope.setExtra(queryKey, params);
            scope.setContext('error', {
              error:
                error instanceof AxiosError ? error.response?.data : undefined,
            });
            scope.setLevel('error');
          });
          throw new Error('Terjadi Kesalahan useQueryGet');
        }
      }
    };

    const { data, isPending, refetch } = useQuery<K>({
      queryKey: [queryKey],
      queryFn: fetchData,
      ...options,
    });

    return {
      data: data ?? [],
      isLoading: isPending,
      refetch,
    };
  }

  function useQueryGetAll<K>(
    queryKey: string,
    payload?: Readonly<QueryStringSearch>,
    cacheTime?: number,
    navigateError?: string
  ) {
    const [pageCount, setPageCount] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);
    const { keyword } = useSearch();

    const queryClient = useQueryClient();
    const params = {
      page,
      size: 25,
      search_query: keyword,
      ...payload,
    };

    const fetchData = async () => {
      try {
        const res = await apiInstance(token).get(url, {
          params: cleanDeep(params),
        });
        setPageCount(Number(res.data.pagination.pages));
        setTotal(Number(res.data.pagination.rows));
        return res.data;
      } catch (error) {
        if (error instanceof AxiosError) {
          if (navigateError) {
            router.push(navigateError);
            buyerCaptureException(error.response?.data.message, (scope) => {
              scope.setTag(queryKey, 'useQueryGetAll error');
              scope.setExtra(queryKey, params);
              scope.setContext('error', {
                error:
                  error instanceof AxiosError
                    ? error.response?.data
                    : undefined,
              });
              scope.setLevel('error');
            });
            throw new Error('Terjadi Kesalahan useQueryGetAll');
          }
        }
      }
    };

    const {
      data,
      isPending,
      isPlaceholderData: isPreviousData,
      refetch,
      isFetching,
    } = useQuery<K>({
      queryKey: [queryKey, page, keyword, cleanDeep(payload)],
      queryFn: fetchData,
      placeholderData: keepPreviousData,
      gcTime: cacheTime,
    });

    useEffect(() => {
      queryClient.prefetchQuery({
        queryKey: [queryKey, page + 1, keyword],
        queryFn: fetchData,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, queryClient]);

    return {
      data: data,
      pageCount,
      total,
      isLoading: isPending,
      isPreviousData,
      isFetching,
      refetch,
    };
  }

  function useQueryGetById<K>(
    id: string,
    queryKey: string,
    payload?: Readonly<QueryStringSearch>,
    retry?: boolean
  ) {
    const params = { ...payload };
    const fetchData = async () => {
      try {
        const res = await apiInstance(token).get(`${url}/${id}`, {
          params: cleanDeep(params),
        });
        return res.data;
      } catch (error) {
        if (error instanceof AxiosError) {
          buyerCaptureException(error.response?.data.message, (scope) => {
            scope.setTag(queryKey, 'useQueryGetById error');
            scope.setExtra(queryKey, { id, ...params });
            scope.setContext('error', {
              error:
                error instanceof AxiosError ? error.response?.data : undefined,
            });
            scope.setLevel('error');
          });
          throw new Error('Terjadi Kesalahan useQueryGetById');
        }
      }
    };
    const cleanedParams = cleanDeep(params);
    const {
      data,
      isPending,
      isPlaceholderData: isPreviousData,
      refetch,
    } = useQuery<K>({
      queryKey: [queryKey, id, cleanedParams],
      queryFn: fetchData,
      enabled: id !== null && id !== undefined && id !== '',
      retry: retry,
    });

    return {
      data: data,
      isLoading: isPending,
      isPreviousData,
      refetch,
    };
  }

  function useQueryData<K>(
    queryKey: string,
    payload?: Readonly<QueryStringSearch>,
    queryOptions: QueryOptions = { prefetchNextPage: true }
  ) {
    const [pageCount, setPageCount] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);
    const { keyword } = useSearch();
    const queryClient = useQueryClient();
    const payloadSignature = JSON.stringify(cleanDeep(payload ?? {}));
    const cleanedPayload = useMemo(
      () => JSON.parse(payloadSignature) as QueryStringSearch,
      [payloadSignature]
    );
    const params = useMemo(
      () => ({
        page,
        size: 25,
        search_query: keyword,
        ...cleanedPayload,
      }),
      [cleanedPayload, keyword]
    );
    const currentPage = Number(params.page ?? 1);
    const nextPageParams = useMemo(
      () =>
        cleanDeep({
          ...params,
          page: currentPage + 1,
        }),
      [currentPage, params]
    );
    const isQueryEnabled = queryOptions.enabled ?? true;
    const shouldPrefetchNextPage =
      queryOptions.prefetchNextPage && isQueryEnabled;

    const fetchData = async (prefetchNextPage = false) => {
      const updatedParams = prefetchNextPage ? nextPageParams : params;
      try {
        const res = await apiInstance(token).get(url, {
          params: cleanDeep(updatedParams),
        });
        if (!prefetchNextPage) {
          setPageCount(Number(res.data.pagination.pages));
          setTotal(Number(res.data.pagination.rows));
        }
        return res.data.data;
      } catch (error) {
        if (error instanceof AxiosError) {
          buyerCaptureException(error.response?.data.message, (scope) => {
            scope.setTag(queryKey, 'useQueryData error');
            scope.setExtra(queryKey, params);
            scope.setContext('error', {
              error:
                error instanceof AxiosError ? error.response?.data : undefined,
            });
            scope.setLevel('error');
          });
          throw new Error('Terjadi Kesalahan useQueryData');
        }
      }
    };

    const {
      data,
      isPending,
      isPlaceholderData: isPreviousData,
      refetch,
      isStale,
      isFetching,
    } = useQuery<K[]>({
      queryKey: [queryKey, currentPage, keyword, cleanedPayload],
      queryFn: () => fetchData(),
      enabled: isQueryEnabled,
      placeholderData: keepPreviousData,
      staleTime: queryOptions.staleTime,
    });

    useEffect(() => {
      if (shouldPrefetchNextPage) {
        queryClient.prefetchQuery({
          queryKey: [queryKey, currentPage + 1, keyword, nextPageParams],
          queryFn: () => fetchData(true),
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      currentPage,
      keyword,
      nextPageParams,
      payloadSignature,
      queryClient,
      queryKey,
      shouldPrefetchNextPage,
    ]);

    return {
      data: data ?? [],
      pageCount,
      total,
      isLoading: isPending,
      isPreviousData,
      isStale,
      isFetching,
      refetch,
    };
  }

  return {
    useQueryGet,
    useQueryGetById,
    useQueryData,
    useQueryGetAll,
  };
}
